import { ArrowCounterClockwiseIcon, ArrowUUpLeftIcon } from '@phosphor-icons/react'
import { useVirtualizer } from '@tanstack/react-virtual'
import React, { useMemo, useRef } from 'react'

import { Icon } from '@/components/ui/icon'

import { OPTION_HEIGHT_ESTIMATE } from './select.constants'
import * as S from './select.styled'
import { DropdownLoader } from './select.styled'
import {
  CheckboxState,
  FlattenedOption,
  SelectDropdownVariant,
  SelectOptionAction,
  SelectOptionValue,
  SelectSize,
  SelectTreeOption,
} from './select.types'
import { SelectOptionItem } from './select-option-item'

interface SelectDropdownProps<V extends SelectOptionValue> {
  dropdownLabel?: string
  dropdownVariant?: SelectDropdownVariant
  flattenedOptions: Array<FlattenedOption<V>>
  floatingProps: Record<string, unknown>
  floatingStyles: React.CSSProperties
  isExpanded: (key: V) => boolean
  isMulti: boolean
  isOptionSelected: (value: V) => boolean
  onOptionClick: (value: V, disabled?: boolean) => void
  onReset?: () => void
  onToggleExpanded: (key: V) => void
  onOptionAction?: (action: SelectOptionAction) => void
  loading: boolean
  setFloating: (node: HTMLElement | null) => void
  size: SelectSize
  /** Only set in tree-checkbox mode. */
  getTreeCheckboxState?: (option: SelectTreeOption<V>) => CheckboxState
  onTreeCheckboxToggle?: (option: SelectTreeOption<V>) => void
  display?: 'default' | 'badges'
}

export const SelectDropdown = <V extends SelectOptionValue>({
  dropdownLabel,
  dropdownVariant = 'default',
  flattenedOptions,
  floatingProps,
  floatingStyles,
  isExpanded,
  isMulti,
  isOptionSelected,
  onOptionClick,
  onReset,
  onToggleExpanded,
  onOptionAction,
  loading,
  setFloating,
  size,
  getTreeCheckboxState,
  onTreeCheckboxToggle,
}: SelectDropdownProps<V>) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const isMenu = dropdownVariant === 'menu'
  const hasAnyExpandable = useMemo(
    () => flattenedOptions.some((fo) => fo.hasChildren),
    [flattenedOptions],
  )

  const virtualizer = useVirtualizer({
    count: flattenedOptions.length,
    estimateSize: () => OPTION_HEIGHT_ESTIMATE[size],
    getScrollElement: () => scrollRef.current,
    overscan: 5,
  })

  return (
    <S.Dropdown ref={setFloating} style={floatingStyles} {...floatingProps}>
      {dropdownLabel && (
        <S.DropdownLabel $isMenu={isMenu} $size={size}>
          {dropdownLabel}
        </S.DropdownLabel>
      )}

      <S.OptionsList ref={scrollRef}>
        {loading && <DropdownLoader align="center" size="small" />}
        {!loading && flattenedOptions.length === 0 && (
          <S.NoOptionsMessage $size={size}>No options found</S.NoOptionsMessage>
        )}
        {flattenedOptions.length > 0 && (
          <S.VirtualListContainer style={{ height: virtualizer.getTotalSize() }}>
            {virtualizer.getVirtualItems().map((virtualItem) => {
              const { option, depth, hasChildren } = flattenedOptions[virtualItem.index]

              return (
                <SelectOptionItem
                  key={String(option.value)}
                  depth={depth}
                  expanded={isExpanded(option.value)}
                  hasAnyExpandable={hasAnyExpandable}
                  hasChildren={hasChildren}
                  isMenu={isMenu}
                  isMulti={isMulti}
                  option={option}
                  selected={isOptionSelected(option.value)}
                  size={size}
                  style={{
                    height: virtualItem.size,
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                  treeCheckboxState={getTreeCheckboxState?.(option)}
                  onOptionClick={onOptionClick}
                  onToggleExpanded={onToggleExpanded}
                  onOptionAction={onOptionAction}
                  onTreeCheckboxToggle={onTreeCheckboxToggle}
                />
              )
            })}
          </S.VirtualListContainer>
        )}
      </S.OptionsList>

      {onReset && (
        <S.ResetButton $isMenu={isMenu} $size={size} type="button" onClick={onReset}>
          {isMenu ? (
            <Icon icon={ArrowUUpLeftIcon} size="2rem" />
          ) : (
            <Icon icon={ArrowCounterClockwiseIcon} size="1.4rem" />
          )}
          Reset to default
        </S.ResetButton>
      )}
    </S.Dropdown>
  )
}
